import React, { useEffect, useMemo, useState } from "react";
import {
  Spin,
  Table,
  message,
  Input,
  Select,
  Row,
  Col,
  Button,
  Alert,
} from "antd";
import { useTranslation } from "react-i18next";
import ReactExport from "react-export-excel";
import moment from "moment";
import requestAdmin from "../../utils/requestAdmin";
import { isAccessDeniedError } from "../../utils/requestHelpers";
import styles from "./Gosuslugy.module.scss";

const { Search } = Input;
const { Option } = Select;
const { ExcelFile } = ReactExport;
const { ExcelSheet, ExcelColumn } = ExcelFile;

const flattenReports = (raw) => {
  if (!raw || typeof raw !== "object") return [];
  return Object.values(raw).flatMap(({ category, data }) =>
    (data || []).map((row, idx) => ({
      key: `${category}-${row.district || ""}-${row.organization || ""}-${
        row.year || ""
      }-${row.month || ""}-${idx}`,
      reportName: category,
      district: row.district || "",
      organization: row.organization || "",
      year: row.year || "",
      month: row.month || "",
      createdAt: row.createdAt
        ? moment(row.createdAt).format("DD.MM.YYYY")
        : "",
    }))
  );
};

const Gosuslugy = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [districtFilter, setDistrictFilter] = useState(null);
  const [yearFilter, setYearFilter] = useState(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    requestAdmin("/myReport/reports", { method: "GET" })
      .then((data) => {
        if (cancelled) return;
        setReports(flattenReports(data));
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAccessDeniedError(err)) {
          setIsAccessDenied(true);
          return;
        }
        message.error(t("gosuslugyPage.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const districts = useMemo(
    () =>
      Array.from(
        new Set(reports.map((r) => r.district).filter(Boolean))
      ).sort(),
    [reports]
  );
  const years = useMemo(
    () =>
      Array.from(new Set(reports.map((r) => r.year).filter(Boolean))).sort(
        (a, b) => Number(b) - Number(a)
      ),
    [reports]
  );

  const filteredReports = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return reports.filter((r) => {
      if (districtFilter && r.district !== districtFilter) return false;
      if (yearFilter && r.year !== yearFilter) return false;
      if (q && !`${r.reportName} ${r.organization}`.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [reports, searchText, districtFilter, yearFilter]);

  const resetFilters = () => {
    setSearchText("");
    setDistrictFilter(null);
    setYearFilter(null);
  };

  const columns = [
    {
      title: t("gosuslugyPage.reportName"),
      dataIndex: "reportName",
      width: "35%",
    },
    {
      title: t("gosuslugyPage.filters.district"),
      dataIndex: "district",
      width: "15%",
    },
    {
      title: t("gosuslugyPage.filters.organization"),
      dataIndex: "organization",
      width: "25%",
    },
    {
      title: t("gosuslugyPage.filters.year"),
      dataIndex: "year",
      width: 100,
      sorter: (a, b) => Number(a.year) - Number(b.year),
      defaultSortOrder: "descend",
    },
    {
      title: t("gosuslugyPage.filters.month"),
      dataIndex: "month",
      width: 120,
    },
    {
      title: t("gosuslugyPage.filters.period"),
      dataIndex: "createdAt",
      width: 130,
    },
  ];

  if (isAccessDenied) {
    return (
      <div className={styles.container}>
        <Alert
          type="error"
          message={t("error.doNotHaveAccessToViewThisModule")}
          showIcon
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Row gutter={16} className={styles.filters}>
        <Col xs={24} sm={12} md={8}>
          <Search
            allowClear
            placeholder={t("gosuslugyPage.filters.service")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder={t("gosuslugyPage.filters.district")}
            value={districtFilter}
            onChange={(v) => setDistrictFilter(v || null)}
          >
            {districts.map((d) => (
              <Option key={d} value={d}>
                {d}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder={t("gosuslugyPage.filters.year")}
            value={yearFilter}
            onChange={(v) => setYearFilter(v || null)}
          >
            {years.map((y) => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Button onClick={resetFilters} block>
            {t("gosuslugyPage.filters.reset")}
          </Button>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <ExcelFile
            filename={`gosuslugy-${moment().format("YYYY-MM-DD")}`}
            element={
              <Button type="primary" block disabled={!filteredReports.length}>
                {t("gosuslugyPage.excel")}
              </Button>
            }
          >
            <ExcelSheet data={filteredReports} name="Gosuslugy">
              <ExcelColumn
                label={t("gosuslugyPage.reportName")}
                value="reportName"
              />
              <ExcelColumn
                label={t("gosuslugyPage.filters.district")}
                value="district"
              />
              <ExcelColumn
                label={t("gosuslugyPage.filters.organization")}
                value="organization"
              />
              <ExcelColumn
                label={t("gosuslugyPage.filters.year")}
                value="year"
              />
              <ExcelColumn
                label={t("gosuslugyPage.filters.month")}
                value="month"
              />
              <ExcelColumn
                label={t("gosuslugyPage.filters.period")}
                value="createdAt"
              />
            </ExcelSheet>
          </ExcelFile>
        </Col>
      </Row>

      <Spin tip={t("gosuslugyPage.loading") + "..."} spinning={loading}>
        <Table
          bordered
          size="middle"
          columns={columns}
          dataSource={filteredReports}
          locale={{ emptyText: t("gosuslugyPage.noData") }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default Gosuslugy;
